import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Zap,
    Bell,
    Radio,
    Activity,
    ShieldCheck,
    Snowflake,
    Wine,
    Package,
    Building2,
    Factory,
    Rocket,
    GraduationCap,
    MapPin,
    CalendarDays,
    Users,
    Cpu,
    Warehouse,
    HeartPulse,
} from "lucide-react";
import { Navbar } from "../components/sections/Navbar";
import { Footer } from "../components/sections/Footer";
import { FloatingContact } from "../components/visuals/FloatingContact";
import { ScrollProgress } from "../components/visuals/ScrollProgress";
import useReveal from "../lib/useReveal";

// ------- Data -------

const PROBLEM_NOTIFICATIONS = [
    { icon: Snowflake, label: "LN₂ LEVEL LOW" },
    { icon: Wine, label: "DRINK TAMPERING SUSPECTED" },
    { icon: Bell, label: "EMERGENCY ASSISTANCE NEEDED" },
    { icon: Activity, label: "EQUIPMENT NEEDS ATTENTION" },
    { icon: ShieldCheck, label: "SAFETY SUPPLY ACCESSED" },
    { icon: Radio, label: "REMOTE ALERT TRIGGERED" },
    { icon: Zap, label: "CUSTOM CONDITION DETECTED" },
];

const PUBLIC_SAFETY = [
    { label: "Overdose Response", to: "/public-safety/overdose-response" },
    { label: "Drink Spike Detection", to: "/public-safety/drink-spike-detection" },
    { label: "Safety Resource Access", to: "/contact?interest=other" },
];

const ALERT_AUTOMATION = [
    { label: "LN₂ Scale + Workflow", to: "/alert-automation#ln2-scale" },
    { label: "Equipment Alerts", to: "/alert-automation#equipment-alerts" },
    { label: "Automated Weight Alerts", to: "/alert-automation#automated-weight-alerts" },
    { label: "Custom Alerts", to: "/alert-automation#custom-alert-systems" },
    { label: "Remote Notifications", to: "/alert-automation#custom-alert-systems" },
    { label: "Custom Automation", to: "/contact?interest=custom-system" },
];

const INDUSTRIES = [
    { icon: Factory, label: "Manufacturing" },
    { icon: Rocket, label: "Aerospace" },
    { icon: Wine, label: "Nightlife" },
    { icon: Users, label: "Hospitality" },
    { icon: Warehouse, label: "Warehouses" },
    { icon: HeartPulse, label: "Healthcare" },
    { icon: GraduationCap, label: "Schools & Campuses" },
    { icon: MapPin, label: "Public Spaces" },
    { icon: CalendarDays, label: "Events" },
    { icon: Building2, label: "Municipal Programs" },
    { icon: Cpu, label: "Industrial Facilities" },
];

const OPERATIONS_CHIPS = [
    "LEVEL LOW",
    "DOOR OPEN",
    "EQUIPMENT DOWN",
    "HELP NEEDED",
    "SUPPLY LOW",
    "CONDITION DETECTED",
];

// ------- Main Page -------

export default function HomePage() {
    useReveal();
    return (
        <main data-testid="home-page" className="min-h-screen bg-white">
            <ScrollProgress />
            <Navbar />
            <Hero />
            <Bridge />
            <Categories />
            <IndustriesStrip />
            <SecondInteractive />
            <FinalCTA />
            <Footer />
            <FloatingContact />
            <HomeStyles />
        </main>
    );
}

// ------- Sections -------

const Hero = () => {
    const [pressed, setPressed] = useState(false);
    const [revealStep, setRevealStep] = useState(0); // 0..PROBLEM_NOTIFICATIONS.length

    // Staggered notification reveal after press
    useEffect(() => {
        if (!pressed) return;
        setRevealStep(0);
        const timers = PROBLEM_NOTIFICATIONS.map((_, i) =>
            setTimeout(() => setRevealStep(i + 1), 180 * (i + 1))
        );
        return () => timers.forEach(clearTimeout);
    }, [pressed]);

    return (
        <section
            id="hero"
            data-testid="home-hero"
            className="relative bg-[#0A1428] text-white overflow-hidden min-h-[100vh] flex items-center pt-24 pb-24"
        >
            {/* Backdrop */}
            <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
            <div className="absolute -top-40 -right-32 w-[640px] h-[640px] opacity-[0.08] pointer-events-none float-slow">
                <svg viewBox="0 0 200 200">
                    <polygon points="20,160 130,60 180,160" fill="#F0493E" />
                </svg>
            </div>
            <div className="absolute -bottom-40 -left-24 w-[540px] h-[540px] opacity-[0.05] pointer-events-none float-slower">
                <svg viewBox="0 0 200 200">
                    <polygon points="20,160 100,40 180,160" fill="#3B82F6" />
                </svg>
            </div>

            <div className="relative w-full max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-x-6 gap-y-16 items-center">
                    {/* Left: copy */}
                    <div className="col-span-12 lg:col-span-6 reveal">
                        <div className="inline-flex items-center gap-3 mb-6">
                            <span className="relative inline-flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-[#F0493E] pulse-ring" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F0493E]" />
                            </span>
                            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                                Kairos ORD LLC
                            </span>
                        </div>

                        <h1 className="font-display font-extrabold text-4xl md:text-5xl lg:text-[68px] leading-[0.97] tracking-tighter text-balance max-w-2xl">
                            There should be
                            <br />
                            a button
                            <br />
                            <span className="text-[#F0493E]">for that.</span>
                        </h1>
                        <p className="mt-8 text-lg md:text-xl text-slate-300 font-medium">
                            Sometimes, there is.
                        </p>
                        <p className="mt-4 max-w-lg text-base md:text-lg text-slate-400 leading-relaxed font-medium">
                            Kairos builds alert, monitoring, and response
                            systems around problems that need a better
                            solution.
                        </p>
                    </div>

                    {/* Right: interactive button */}
                    <div className="col-span-12 lg:col-span-6">
                        <div className="reveal relative flex flex-col items-center justify-center">
                            <div className="mb-6 text-[11px] md:text-xs font-bold uppercase tracking-[0.32em] text-slate-400">
                                {pressed ? "System active" : "Have a problem?"}
                            </div>
                            <KairosButton
                                pressed={pressed}
                                onPress={() => setPressed(true)}
                                revealStep={revealStep}
                            />

                            {/* Post-press message */}
                            <div
                                className={`mt-10 text-center transition-all duration-700 ${
                                    revealStep >= PROBLEM_NOTIFICATIONS.length
                                        ? "opacity-100 translate-y-0"
                                        : "opacity-0 translate-y-2 pointer-events-none"
                                }`}
                            >
                                <a
                                    href="#bridge"
                                    data-testid="hero-scroll-cue"
                                    className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.32em] text-white/70 hover:text-white"
                                >
                                    See what we solve
                                    <ArrowRight size={14} strokeWidth={2} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const KairosButton = ({ pressed, onPress, revealStep }) => {
    // 7 notifications arranged in a rough ring
    const positions = useMemo(
        () => [
            { top: "-14%", left: "50%", x: "-50%" },        // top
            { top: "8%", left: "108%", x: "-50%" },         // top-right
            { top: "50%", left: "116%", x: "-50%", y: "-50%" }, // right
            { top: "92%", left: "108%", x: "-50%" },        // bottom-right
            { top: "114%", left: "50%", x: "-50%" },        // bottom
            { top: "92%", left: "-8%", x: "-50%" },         // bottom-left
            { top: "50%", left: "-16%", x: "-50%", y: "-50%" }, // left
        ],
        []
    );

    return (
        <div className="relative w-[280px] h-[280px] md:w-[340px] md:h-[340px]">
            {/* Concentric rings — always subtle, more visible after press */}
            <span
                className={`absolute inset-0 rounded-full border ${
                    pressed ? "border-[#F0493E]/40" : "border-white/10"
                } transition-colors duration-700`}
            />
            <span
                className={`absolute inset-[-14%] rounded-full border ${
                    pressed ? "border-[#F0493E]/15" : "border-white/5"
                } transition-colors duration-700`}
            />
            <span
                className={`absolute inset-[-28%] rounded-full border ${
                    pressed ? "border-[#F0493E]/8" : "border-white/[0.03]"
                } transition-colors duration-700`}
            />

            {/* Ambient pulse (before press) */}
            {!pressed && (
                <>
                    <span className="absolute inset-[-6%] rounded-full bg-[#F0493E]/10 kairos-idle-pulse" />
                    <span className="absolute inset-[-6%] rounded-full bg-[#F0493E]/8 kairos-idle-pulse-2" />
                </>
            )}

            {/* Notification cards — appear after press */}
            {positions.map((pos, i) => (
                <Notification
                    key={PROBLEM_NOTIFICATIONS[i].label}
                    icon={PROBLEM_NOTIFICATIONS[i].icon}
                    label={PROBLEM_NOTIFICATIONS[i].label}
                    visible={revealStep > i}
                    style={{
                        top: pos.top,
                        left: pos.left,
                        transform: `translate(${pos.x || "-50%"}, ${pos.y || "0"})`,
                    }}
                    testid={`hero-notification-${i}`}
                />
            ))}

            {/* Physical button */}
            <button
                data-testid="hero-kairos-button"
                onClick={onPress}
                aria-label="Press to see what Kairos solves"
                className={`kairos-btn absolute inset-[15%] rounded-full flex flex-col items-center justify-center gap-2 focus:outline-none ${
                    pressed ? "kairos-btn-pressed" : ""
                }`}
            >
                {/* Inner face */}
                <div className="kairos-btn-face rounded-full absolute inset-0" />
                {/* Rim highlight */}
                <div className="kairos-btn-rim rounded-full absolute inset-0" />

                {/* Face content */}
                <div className="relative flex flex-col items-center justify-center z-10 px-6 text-center">
                    <div
                        className={`h-2 w-2 rounded-full mb-3 ${
                            pressed ? "bg-[#F0493E]" : "bg-[#F0493E]/70"
                        } shadow-[0_0_10px_2px_rgba(240,73,62,0.55)]`}
                    />
                    <div className="font-display font-extrabold text-white text-base md:text-lg tracking-[0.2em] uppercase leading-tight">
                        {pressed ? "Kairos" : "Press"}
                    </div>
                    <div className="mt-1 text-[9px] md:text-[10px] font-bold uppercase tracking-[0.28em] text-white/60 leading-tight">
                        {pressed ? "System active" : "To see what we solve"}
                    </div>
                </div>
            </button>
        </div>
    );
};

const Notification = ({ icon: Icon, label, visible, style, testid }) => (
    <div
        data-testid={testid}
        style={style}
        className={`absolute z-20 pointer-events-none transition-all duration-500 ease-out ${
            visible
                ? "opacity-100 scale-100 blur-0"
                : "opacity-0 scale-90 blur-[2px]"
        }`}
    >
        <div className="flex items-center gap-2.5 bg-[#0F1E3A] border border-[#F0493E]/40 px-3.5 py-2 shadow-[0_10px_30px_-10px_rgba(240,73,62,0.35)]">
            <div className="h-1.5 w-1.5 rounded-full bg-[#F0493E] shrink-0" />
            <Icon size={13} strokeWidth={1.75} className="text-[#F0493E] shrink-0" />
            <span className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.18em] text-white whitespace-nowrap">
                {label}
            </span>
        </div>
    </div>
);

const Bridge = () => (
    <section
        id="bridge"
        data-testid="home-bridge"
        className="relative bg-white py-24 md:py-32 border-b border-slate-200"
    >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <div className="reveal">
                <div className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                    The Idea
                </div>
                <h2 className="mt-6 font-display font-extrabold text-4xl md:text-6xl leading-[0.98] tracking-tighter text-[#0A1428] text-balance">
                    Different problems.
                    <br />
                    <span className="text-[#F0493E]">Same idea.</span>
                </h2>
            </div>
            <div className="reveal mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    { n: "01", t: "Detect it." },
                    { n: "02", t: "Alert someone." },
                    { n: "03", t: "Respond faster." },
                ].map((s) => (
                    <div
                        key={s.n}
                        data-testid={`bridge-step-${s.n}`}
                        className="bg-slate-50 border border-slate-200 p-8 md:p-10 text-left hover:border-[#F0493E] transition-colors"
                    >
                        <div className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#F0493E]">
                            Step {s.n}
                        </div>
                        <div className="mt-5 font-display font-extrabold text-3xl md:text-4xl tracking-tighter text-[#0A1428]">
                            {s.t}
                        </div>
                    </div>
                ))}
            </div>
            <div className="reveal mt-16 font-display font-bold text-xl md:text-2xl text-slate-600 tracking-tight">
                Kairos builds the system around the problem.
            </div>
        </div>
    </section>
);

const Categories = () => (
    <section
        id="solutions"
        data-testid="home-categories"
        className="relative bg-[#0A1428] text-white py-24 md:py-32 overflow-hidden"
    >
        <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <div className="reveal max-w-3xl">
                <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                    Where Kairos Works
                </span>
                <h2 className="mt-6 font-display font-extrabold text-4xl md:text-6xl leading-[0.98] tracking-tighter text-balance">
                    Two categories.
                    <br />
                    One platform.
                </h2>
            </div>

            <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CategoryCard
                    testid="category-public-safety"
                    kicker="Public Safety"
                    title="For the moments that matter to people."
                    items={PUBLIC_SAFETY}
                />
                <CategoryCard
                    testid="category-alert-automation"
                    kicker="Alert & Automation"
                    title="For the systems that keep operations running."
                    items={ALERT_AUTOMATION}
                />
            </div>
        </div>
    </section>
);

const CategoryCard = ({ kicker, title, items, testid }) => (
    <div
        data-testid={testid}
        className="reveal bg-[#0F1E3A] border border-white/10 p-8 md:p-10 hover:border-[#F0493E]/60 transition-colors"
    >
        <div className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#F0493E]">
            {kicker}
        </div>
        <h3 className="mt-6 font-display font-extrabold text-2xl md:text-3xl leading-tight tracking-tight text-white">
            {title}
        </h3>
        <ul className="mt-8 divide-y divide-white/10 border-t border-white/10">
            {items.map((it) => (
                <li key={it.label}>
                    <Link
                        to={it.to}
                        data-testid={`${testid}-item-${it.label
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")}`}
                        className="group flex items-center justify-between gap-4 py-4 text-white/90 hover:text-white transition-colors"
                    >
                        <span className="font-display font-bold text-lg md:text-xl tracking-tight">
                            {it.label}
                        </span>
                        <ArrowRight
                            size={18}
                            strokeWidth={2}
                            className="text-[#F0493E] opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                        />
                    </Link>
                </li>
            ))}
        </ul>
    </div>
);

const IndustriesStrip = () => (
    <section
        id="industries"
        data-testid="home-industries"
        className="relative bg-white py-24 md:py-32 border-b border-slate-200"
    >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="reveal max-w-3xl">
                <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                    Adaptable
                </span>
                <h2 className="mt-6 font-display font-extrabold text-4xl md:text-6xl leading-[0.98] tracking-tighter text-[#0A1428] text-balance">
                    One idea.
                    <br />
                    A lot of places
                    <br />
                    it can work.
                </h2>
            </div>

            <div className="reveal mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                {INDUSTRIES.map((ind, i) => (
                    <div
                        key={ind.label}
                        data-testid={`industry-${i}`}
                        className="group bg-white p-5 md:p-6 flex items-center gap-3 hover:bg-slate-50 transition-colors"
                    >
                        <div className="h-9 w-9 grid place-items-center border border-[#0A1428] group-hover:border-[#F0493E] transition-colors shrink-0">
                            <ind.icon size={16} strokeWidth={1.5} className="text-[#F0493E]" />
                        </div>
                        <div className="font-display font-bold text-sm md:text-[15px] text-[#0A1428] tracking-tight leading-snug">
                            {ind.label}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const SecondInteractive = () => {
    const [pressed, setPressed] = useState(false);
    const chipRef = useRef(null);

    return (
        <section
            data-testid="home-second-interactive"
            className="relative bg-[#0A1428] text-white py-28 md:py-40 overflow-hidden"
        >
            <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
            <div className="absolute -top-40 -left-24 w-[520px] h-[520px] opacity-[0.06] pointer-events-none float-slower">
                <svg viewBox="0 0 200 200">
                    <polygon points="20,160 130,60 180,160" fill="#F0493E" />
                </svg>
            </div>

            <div className="relative max-w-6xl mx-auto px-6 md:px-12 text-center">
                <div className="reveal">
                    <div className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                        Your Operation
                    </div>
                    <h2 className="mt-6 font-display font-extrabold text-3xl md:text-5xl lg:text-6xl leading-[1] tracking-tighter text-balance">
                        {pressed ? (
                            <>
                                That&apos;s
                                <br />
                                <span className="text-[#F0493E]">where we start.</span>
                            </>
                        ) : (
                            <>
                                What does your operation
                                <br />
                                need to know?
                            </>
                        )}
                    </h2>
                </div>

                {/* Ring of chips + button */}
                <div className="reveal relative mt-16 md:mt-20 flex items-center justify-center">
                    <div className="relative w-[300px] h-[300px] md:w-[380px] md:h-[380px]" ref={chipRef}>
                        {OPERATIONS_CHIPS.map((c, i) => {
                            const angle = (i / OPERATIONS_CHIPS.length) * Math.PI * 2 - Math.PI / 2;
                            const r = 50; // percent
                            const x = 50 + Math.cos(angle) * r;
                            const y = 50 + Math.sin(angle) * r;
                            return (
                                <div
                                    key={c}
                                    data-testid={`operations-chip-${i}`}
                                    style={{
                                        top: `${y}%`,
                                        left: `${x}%`,
                                        transform: "translate(-50%, -50%)",
                                        transitionDelay: `${i * 90}ms`,
                                    }}
                                    className={`absolute transition-all duration-500 ease-out ${
                                        pressed
                                            ? "opacity-0 scale-75"
                                            : "opacity-100 scale-100"
                                    }`}
                                >
                                    <div className="border border-white/20 bg-[#0F1E3A]/80 px-3 py-1.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 whitespace-nowrap">
                                        {c}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Center button */}
                        <button
                            data-testid="second-interactive-button"
                            onClick={() => setPressed((p) => !p)}
                            className={`kairos-btn kairos-btn-sm absolute rounded-full inset-[24%] flex flex-col items-center justify-center focus:outline-none ${
                                pressed ? "kairos-btn-pressed" : ""
                            }`}
                            aria-label="Press to build your system"
                        >
                            <div className="kairos-btn-face rounded-full absolute inset-0" />
                            <div className="kairos-btn-rim rounded-full absolute inset-0" />
                            <div className="relative z-10 flex flex-col items-center px-4 text-center">
                                <div className="h-1.5 w-1.5 rounded-full bg-[#F0493E] shadow-[0_0_8px_2px_rgba(240,73,62,0.55)]" />
                                <div className="mt-2 font-display font-extrabold text-white text-sm md:text-base tracking-[0.2em] uppercase">
                                    {pressed ? "Ready" : "Press"}
                                </div>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Post-press content */}
                <div
                    className={`mt-14 md:mt-16 transition-all duration-700 ${
                        pressed
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 translate-y-3 pointer-events-none"
                    }`}
                >
                    <p className="font-display font-bold text-xl md:text-2xl text-white/90 tracking-tight">
                        Tell us the problem.
                        <br />
                        We&apos;ll build around it.
                    </p>
                    <Link
                        to="/contact"
                        data-testid="second-interactive-cta"
                        className="btn-primary mt-8 inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm"
                    >
                        Build your system
                        <ArrowRight size={18} strokeWidth={2} />
                    </Link>
                </div>
            </div>
        </section>
    );
};

const FinalCTA = () => (
    <section
        data-testid="home-final-cta"
        className="relative bg-white py-24 md:py-32 border-b border-slate-200"
    >
        <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="reveal border border-slate-900 p-10 md:p-16 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
                <div>
                    <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.32em] text-[#F0493E]">
                        Final Question
                    </div>
                    <h2 className="mt-6 font-display font-extrabold text-3xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tighter text-[#0A1428] text-balance">
                        What problem can
                        <br />
                        we solve for you?
                    </h2>
                </div>
                <Link
                    to="/contact"
                    data-testid="final-cta-button"
                    className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm self-start md:self-end whitespace-nowrap"
                >
                    Build your system
                    <ArrowRight size={18} strokeWidth={2} />
                </Link>
            </div>
        </div>
    </section>
);

// Injected once — physical Kairos button styling with pressable feel.
const HomeStyles = () => (
    <style>{`
        .kairos-btn {
            cursor: pointer;
            transform: translateZ(0);
            transition: transform 180ms cubic-bezier(.2,.7,.2,1);
        }
        .kairos-btn:hover {
            transform: translateY(-2px);
        }
        .kairos-btn:active {
            transform: translateY(1px) scale(.98);
        }
        .kairos-btn-pressed {
            transform: translateY(1px) scale(.985);
        }

        /* Deep face */
        .kairos-btn .kairos-btn-face {
            background: radial-gradient(
                circle at 50% 32%,
                #1A2E52 0%,
                #0F1E3A 55%,
                #06101F 100%
            );
            box-shadow:
                inset 0 -14px 30px rgba(0,0,0,0.6),
                inset 0 12px 24px rgba(255,255,255,0.05),
                0 30px 60px -20px rgba(240,73,62,0.35),
                0 8px 20px -10px rgba(0,0,0,0.7);
        }
        .kairos-btn-pressed .kairos-btn-face {
            background: radial-gradient(
                circle at 50% 40%,
                #10203E 0%,
                #0A1730 60%,
                #030913 100%
            );
            box-shadow:
                inset 0 8px 24px rgba(0,0,0,0.75),
                inset 0 -4px 10px rgba(255,255,255,0.03),
                0 12px 30px -12px rgba(240,73,62,0.55);
        }

        /* Rim / bezel */
        .kairos-btn .kairos-btn-rim {
            border: 1px solid rgba(255,255,255,0.08);
            pointer-events: none;
        }
        .kairos-btn:hover .kairos-btn-rim {
            border-color: rgba(240,73,62,0.35);
            box-shadow: 0 0 0 1px rgba(240,73,62,0.15);
        }

        @keyframes kairosIdlePulse {
            0%   { transform: scale(1);   opacity: 0.35; }
            80%  { transform: scale(1.28); opacity: 0; }
            100% { transform: scale(1.28); opacity: 0; }
        }
        .kairos-idle-pulse {
            animation: kairosIdlePulse 3.4s ease-out infinite;
            pointer-events: none;
        }
        .kairos-idle-pulse-2 {
            animation: kairosIdlePulse 3.4s ease-out 1.7s infinite;
            pointer-events: none;
        }
    `}</style>
);
